import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Loader2, RotateCcw, Send } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import {
  LEVEL_WORDS, ORIENTATIVE, eventLine, modeLine, plusDays, reopenQuestion, requestLine, resendQuestion,
  stateLine,
} from "../../../lib/initialAssessmentAdmin";
import DeadlineDialog from "./DeadlineDialog";
import Answers from "../../assessment/Answers";
import { TONE } from "./GroupAssessmentPanel";

/**
 * One participant's initial assessment, on the enrolment's sheet (15.10.2026):
 * its state, the orientative level, the answers, the history, a reopening
 * request, and the one button that fits — send, send again, or reopen.
 * Owner and admin only, as on the server.
 */
export default function EnrollmentAssessmentPanel({ enrollmentId, name, email, canMutate }) {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/admin/enrollments/${enrollmentId}/initial-assessment`)).data);
    } catch {
      setData({ failed: true });
    }
  }, [enrollmentId]);
  useEffect(() => { load(); }, [load]);

  if (!data || data.failed || !data.applies) return null;

  const record = data.record || {};
  const state = data.state;
  const line = stateLine(state, record);
  const external = data.mode?.mode === "external";
  const score = record.score || {};
  const action = state === "closed" ? "reopen" : state === "open" ? "resend" : state === "not_sent" ? "send" : null;

  const send = async (day) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/initial-assessment/send`, { deadline: day });
      toast.success(r.data.kind === "reopened" ? "Am redeschis chestionarul și am trimis linkul nou." : "Am trimis linkul.");
      setDialog(false);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut trimite chestionarul."));
    } finally {
      setBusy(false);
    }
  };

  const LABEL = { send: "Trimite chestionarul", resend: "Retrimite linkul", reopen: "Redeschide și retrimite" };

  return (
    <section className="rounded-2xl border border-[#E5E7EB] p-4" data-testid="enrollment-assessment-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7]">
          <ClipboardList size={14} /> Evaluare inițială
        </h3>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[line.tone]}`} data-testid="enrollment-assessment-state">
          {line.text}
        </span>
      </div>

      {(external || !data.mode?.mode) && (
        <p className="mt-2 text-xs text-[#6B7280]">Pentru grupa lui: {modeLine(data.mode)}.</p>
      )}

      {record.request?.at && state === "closed" && (
        <p className="mt-2 rounded-lg bg-[#FDF6E3] px-3 py-2 text-sm text-[#8a6a1a]" data-testid="enrollment-assessment-request">
          {requestLine(name, record.request.at)}
        </p>
      )}

      {score.level && (
        <div className="mt-3 text-sm text-[#373A45]">
          Nivel inițial: <strong>{LEVEL_WORDS[score.level]}</strong> <span className="italic text-[#6B7280]">(orientativ)</span>
          {score.mean != null && <span className="text-[#6B7280]"> · media {String(score.mean).replace(".", ",")}</span>}
          <p className="mt-0.5 text-[11px] text-[#6B7280]">{ORIENTATIVE}</p>
        </div>
      )}

      {canMutate && action && !external && (
        <button type="button" onClick={() => setDialog(true)}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#965EA7] px-3 py-1.5 text-xs font-semibold text-[#7A4986] hover:bg-[#F0E7F4]"
          data-testid={`enrollment-assessment-${action}`}>
          {action === "reopen" ? <RotateCcw size={12} /> : <Send size={12} />} {LABEL[action]}
        </button>
      )}

      {record.answers && (
        <details className="mt-3 rounded-xl bg-[#F5F6F6] p-3" data-testid="enrollment-assessment-answers">
          <summary className="cursor-pointer text-sm font-semibold text-[#373A45]">Răspunsurile</summary>
          <Answers questionnaire={data.questionnaire} answers={record.answers} />
        </details>
      )}

      {(record.events || []).length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-semibold text-[#6B7280]">Istoric</summary>
          <ul className="mt-1 grid gap-0.5 text-[11px] text-[#6B7280]">
            {record.events.slice().reverse().map((e, i) => <li key={i}>{eventLine(e)}</li>)}
          </ul>
        </details>
      )}

      <DeadlineDialog
        open={dialog}
        title={LABEL[action] || ""}
        withDate={action === "reopen"}
        question={(day, unchanged) => (action === "reopen" ? reopenQuestion(name, day, unchanged ? 7 : null)
          : action === "resend" ? resendQuestion(email)
            : `Trimiți chestionarul către ${email}? Termenul e cel obișnuit al grupei.`)}
        initialDay={plusDays(7)}
        confirmLabel={action === "reopen" ? "Redeschide și trimite" : "Trimite"}
        busy={busy}
        onConfirm={send}
        onClose={() => setDialog(false)}
      />
    </section>
  );
}
